/**
 * Report delivery-state copy for legacy reader surfaces.
 *
 * The historical `isDraft` flag is overloaded: genuine low-cost legacy
 * drafts set it, but the quality gate also sets it when a full-quality run is
 * held for revision.  UI code must therefore never use `isDraft` (or a broad
 * export-bar predicate) as shorthand for "show Finalize".  Only an explicit
 * persisted `generationTier: 'draft'`, after higher-priority failure states
 * are ruled out, is a legacy draft that can truthfully be finalized.
 */
import type { SiteRagReport } from '../types/siteRag';
import { isExportableTier } from './export/draftExportGate';
import { isAuditAdvisoryDeliveryReason } from './reportGeneration/evaluateReportDelivery';
import { reevaluateStampedHoldFindings } from './audit/reevaluateQualityHold';
import {
  flaggedFloorClause,
  liveQualityHoldFindings,
  openAuditBannerSummary,
  openAuditFindings,
  type OpenAuditFindings,
} from './audit/openAuditFlag';
import {
  isWithheldNarrativeHoldFinding,
  withheldNarrativeHoldFinding,
  withheldNarrativeHoldMessage,
  withheldNarrativeLeaves,
  type WithheldNarrativeLeaf,
} from './report/withheldNarrative';

export type DraftTierUxKind =
  | 'ready'
  /**
   * AUDIT-ADVISORY (2026-08-22, William) — the report is fully deliverable,
   * but its quality audit flagged findings (a stamped hold, a reviewer
   * needs-revision verdict, or open truth questions). The report page's
   * header status chip reads "Audit recommended"; nothing is barred.
   */
  | 'audit-recommended'
  | 'legacy-draft'
  /**
   * Held for revision. Barred (`exportBarred: true`) only for the legacy
   * transitional stamps handled in the barred branch; a WITHHELD NARRATIVE
   * hold (owner decision 2026-09-18) is a deliverable `quality-hold` with
   * `exportBarred: false` — held, never export-disabled.
   */
  | 'quality-hold'
  | 'degraded'
  | 'invalid'
  | 'incomplete';

export interface DraftTierUx {
  kind: DraftTierUxKind;
  exportBarred: boolean;
  /** Reason shown beneath disabled PDF / Word / PowerPoint menu rows. */
  exportHint?: string;
  /** Copy for the non-document delivery state — the report header status
   *  chip's detail (a banner until 2026-10-13). */
  bannerTitle?: string;
  bannerDetail?: string;
  findings: readonly string[];
  /**
   * How many findings STAND behind `findings` — deliberately NOT
   * `findings.length`, which is capped (OPEN_AUDIT_MAX_FINDINGS) and then
   * truncated again by whatever the banner has room for. Surfaces reconcile
   * the two through `openAuditListNote`; they must never subtract one
   * rendered length from another to get a remainder.
   */
  findingCount: number;
  /** Primary recovery action appropriate to this exact state. */
  action: 'finalize' | 'review-quality' | 'refresh-narrative' | 'rerun' | null;
}

const INVALID_REASON_CODES = new Set([
  'schema-invalid',
  'hard-gate-failed',
  'layout-invalid',
  'projection-incomplete',
  'retired-evidence',
]);

const INCOMPLETE_REASON_CODES = new Set([
  'artifact-unavailable',
  'layout-pending',
  'legacy-validation-required',
  'draft',
]);

const BODY_UNAVAILABLE_REASON_CODES = new Set(['body-unavailable', 'metadata-only']);

/**
 * WITHHELD NARRATIVE HOLD (owner decision 2026-09-18, "Allow export").
 *
 * A leaf the reviewer-voice gate withheld HOLDS the report for revision but no
 * longer bars export, so the banner reads as the hold it is — "Narrative
 * withheld · needs revision" — never "export disabled". It lists the hold's
 * own findings first (one per withheld leaf, in the stamped wording, derived
 * from the withheld record so they show even before the hold is stamped),
 * then any other open audit findings, and never lists a stamped withheld
 * finding twice. `findingCount` stays the honest total: the withheld leaves
 * plus what `openAuditFindings` counts BESIDES the stamped withheld findings.
 */
function withheldNarrativeHoldUx(
  report: SiteRagReport,
  leaves: readonly WithheldNarrativeLeaf[],
  audit: OpenAuditFindings,
): DraftTierUx {
  const withheldFindings = leaves.map(withheldNarrativeHoldFinding);
  const otherFindings = audit.flag
    ? audit.findings.filter((finding) => !isWithheldNarrativeHoldFinding(finding))
    : [];
  // 'audit-finding' counts the stamped withheld findings AND reviewer issues;
  // 'truth-disagreement' never counts withheld stamps (openAuditFindings);
  // 'flagged' is the never-disclose-zero floor, i.e. nothing else stands.
  const stampedWithheldCount = liveQualityHoldFindings(report).filter(isWithheldNarrativeHoldFinding).length;
  const otherCount = !audit.flag || audit.kind === 'flagged'
    ? 0
    : audit.kind === 'audit-finding'
      ? Math.max(otherFindings.length, audit.count - stampedWithheldCount)
      : audit.count;
  return {
    kind: 'quality-hold',
    exportBarred: false,
    exportHint: 'Narrative withheld — regenerate the report to restore it. Exports show the “not assessed” notice in its place.',
    bannerTitle: 'Narrative withheld · needs revision',
    bannerDetail: `${withheldNarrativeHoldMessage(leaves)} Reading and export remain available; every export shows the same “not assessed” notice at the withheld section.`,
    findings: [...withheldFindings, ...otherFindings],
    findingCount: withheldFindings.length + otherCount,
    action: 'review-quality',
  };
}

/**
 * Resolve mutually-exclusive delivery UX with truthful recovery copy.
 *
 * Priority matters. A report can be both `isDraft` and quality-held, or both
 * degraded and stamped with the old draft flag. Those reports must expose the
 * quality/narrative recovery workflow rather than being mislabeled as a cheap
 * draft that merely needs "Finalize".
 */
export function resolveDraftTierUx(report: SiteRagReport): DraftTierUx {
  const exportBarred = !isExportableTier(report);
  if (!exportBarred) {
    // Deliverable — but a stamped hold / reviewer verdict still deserves the
    // page's one subtle notice (audit-advisory policy: holds never bar, they
    // recommend). Reuses the same re-evaluation the barred branch runs so the
    // findings shown are the ones the CURRENT rules still stand behind.
    // ONE predicate + ONE finding list for every disclosure surface
    // (openAuditFlag) — this now also covers blocking truth violations that
    // never received a hold stamp, which delivery admits and must disclose.
    const audit = openAuditFindings(report);
    // A withheld narrative leaf is a HOLD (2026-09-18): it takes the hold
    // banner, never the softer "audit recommended" notice, and never bars.
    const withheld = withheldNarrativeLeaves(report);
    if (withheld.length > 0) return withheldNarrativeHoldUx(report, withheld, audit);
    if (audit.flag) {
      /*
       * THE FLOOR IS NOT A FINDING (round 8, 2026-08-25). On a flagged-floor
       * report — `kind: 'flagged'`, `findings: []`, `count` the
       * never-disclose-zero floor of 1 — this title read "1 finding to review"
       * over a banner that then renders NO bullets, while the counsel DOCX and
       * the deck told the reader of the SAME report that no individual finding
       * stands. The title clause and the sentence now branch on the same `kind`
       * from the same module.
       *
       * The lead is this surface's house casing (the DOCX cover uppercases
       * its own); only the clause that makes a claim is shared. Null when the
       * builder has nothing to say — `flag` implies `count >= 1` inside
       * `openAuditFindings`, but that is its invariant, not this call's, so
       * the null is handled rather than asserted away.
       */
      const summary = openAuditBannerSummary(audit);
      const floorClause = flaggedFloorClause();
      return {
        kind: 'audit-recommended',
        exportBarred: false,
        bannerTitle: summary ? `Audit recommended — ${summary}` : 'Audit recommended',
        bannerDetail: audit.kind === 'flagged'
          // Nothing itemised stands, so "review the findings" would send the
          // reader to a bullet list this banner does not render.
          ? `The quality audit flagged this analysis. ${floorClause.charAt(0).toUpperCase()}${floorClause.slice(1)}, `
            + 'so there is nothing itemised to review — reading, export, '
            + 'and the Go/No-Go verdict all remain available.'
          : report.qualityHold === 'needs-revision'
            ? 'Authoritative Quality Hold — Needs Revision. Resolve or verify the open audit findings before relying on this report for decision use.'
            : 'The quality audit flagged this analysis. Review the findings before relying on it — '
              + 'reading, export, and the Go/No-Go verdict all remain available.',
        findings: audit.findings,
        findingCount: audit.count,
        action: 'review-quality',
      };
    }
    return { kind: 'ready', exportBarred: false, findings: [], findingCount: 0, action: null };
  }

  const stampedFindings = (Array.isArray(report.qualityHoldFindings) ? report.qualityHoldFindings : []).filter(
    (finding): finding is string => typeof finding === 'string' && finding.trim().length > 0,
  );
  const reviewerFindings = (Array.isArray(report.qualityReview?.issues) ? report.qualityReview.issues : [])
    .map((issue, idx) => {
      if (!issue || typeof issue !== 'object') return null;
      const rec = issue as { section?: unknown; issue?: unknown; description?: unknown; message?: unknown };
      const text =
        typeof rec.issue === 'string' && rec.issue.trim().length > 0
          ? rec.issue.trim()
          : typeof rec.description === 'string' && rec.description.trim().length > 0
            ? rec.description.trim()
            : typeof rec.message === 'string' && rec.message.trim().length > 0
              ? rec.message.trim()
              : '';
      if (!text) return null;
      const section =
        typeof rec.section === 'string' && rec.section.trim().length > 0
          ? rec.section.trim()
          : `issue-${idx + 1}`;
      return `${section}: ${text}`;
    })
    .filter((entry): entry is string => Boolean(entry));
  const rawPersistedReasons = report.reportRevision?.deliveryDecision?.reasons;
  const persistedReasons = Array.isArray(rawPersistedReasons)
    ? rawPersistedReasons.filter((reason) => (
      Boolean(reason)
      && typeof reason.code === 'string'
      && typeof reason.message === 'string'
    ))
    : [];
  const persistedCodes = new Set(persistedReasons.map((reason) => reason.code));
  /*
   * The LLM reviewer is ADVISORY (severity policy, 2026-08-05) — its verdict
   * alone must NOT bar export. It used to: a `needs-revision` verdict set
   * `quality-hold` with `exportBarred: true` even when the report carried zero
   * truth violations, which is how the live Redhead run (score 74,
   * needs-revision, accurate narrative) presented as undeliverable.
   *
   * `report.qualityHold` is already truth-only — `resolveQualityHold` returns
   * null unless a demonstrable untruth is present — and `quality-held` is the
   * persisted decision. Reviewer issues still surface on the Audit tab's
   * Report-quality block; they simply no longer gate delivery.
   */
  const qualityHeld = report.qualityHold === 'needs-revision'
    || persistedCodes.has('quality-held');

  // AUDIT-ADVISORY (2026-08-22): holds never bar, so when export IS barred the
  // cause is an artifact reason — classify by it first and never attribute the
  // bar to the hold. The hold-copy branch below survives only for stamped
  // states that carry NO other reason (transitional data).
  const hasArtifactBarReason = persistedReasons.some(
    (reason) => !isAuditAdvisoryDeliveryReason(reason),
  ) || report._metadataOnly || report.degradedMode || report.generationTier === 'draft'
    // A LEGACY body (no revision) that is still barred while holding an
    // audit stamp is barred by the unvalidated body, not the hold.
    || !report.reportRevision;

  if (qualityHeld && !hasArtifactBarReason) {
    // Stamps are frozen at generation; the RULES are not. Re-evaluate the
    // stamped strings against the current pure rule layer so the banner
    // shows the live defects, not verdicts a since-fixed rule produced
    // (2026-08-13: a Redhead banner listed 4 findings of which 3 were
    // sentences the current absence gate exempts and 1 was a duplicate).
    // The hold itself still clears only via regeneration — revisions are
    // immutable — but the STORY the banner tells must be current.
    const reeval = reevaluateStampedHoldFindings(report, stampedFindings);
    const findings = reeval.findings.length > 0 || stampedFindings.length > 0
      ? reeval.findings
      : reviewerFindings.length > 0
        ? reviewerFindings
        : persistedReasons
          .filter((reason) => reason.code === 'quality-held')
          .map((reason) => reason.message);
    const allSuperseded = stampedFindings.length > 0 && reeval.findings.length === 0;
    // TWO DIFFERENT STORIES (2026-08-14) — never merge them. "Superseded by
    // updated quality rules" means the gate improved; a DUPLICATE means the
    // same sentence was stamped from several sections and nothing changed.
    // The merged sentence told an operator that 15 findings had been cleared
    // by better rules when all 15 were repeats of the 5 still shown.
    const supersededNote = reeval.supersededCount > 0
      ? ` ${reeval.supersededCount} stamped finding${reeval.supersededCount === 1 ? ' is' : 's are'} superseded by updated quality rules and no longer appl${reeval.supersededCount === 1 ? 'ies' : 'y'}.`
      : '';
    const duplicateNote = reeval.duplicateCount > 0
      ? ` ${reeval.duplicateCount} further stamp${reeval.duplicateCount === 1 ? ' was a' : 's were'} repeat mention${reeval.duplicateCount === 1 ? '' : 's'} of the same finding${reeval.duplicateCount === 1 ? '' : 's'}.`
      : '';
    return {
      kind: 'quality-hold',
      exportBarred: true,
      exportHint: 'Held for revision — review the quality findings and rerun the report before export.',
      bannerTitle: allSuperseded
        ? 'Held by superseded quality rules · export disabled'
        : `Held for revision${findings.length ? ` — ${findings.length} finding${findings.length === 1 ? '' : 's'}` : ''} · export disabled`,
      bannerDetail: allSuperseded
        ? 'Every finding behind this hold is superseded by updated quality rules — regenerate the report once to clear the hold and restore export.'
        : `This report's final quality checks require revision. Review the findings, then rerun the report to clear the hold.${supersededNote}${duplicateNote}`,
      findings,
      // The barred branch's list is re-evaluated live and uncapped, so it IS
      // the honest total here; the banner still lists only the first few.
      findingCount: findings.length,
      action: 'review-quality',
    };
  }

  if (report.degradedMode || persistedCodes.has('degraded')) {
    return {
      kind: 'degraded',
      exportBarred: true,
      exportHint: 'Narrative unavailable — refresh the narrative before export.',
      findings: [],
      findingCount: 0,
      action: 'refresh-narrative',
    };
  }

  const invalidReason = persistedReasons.find(
    (reason) => INVALID_REASON_CODES.has(reason.code) && !isAuditAdvisoryDeliveryReason(reason),
  );
  /*
   * TRUTH-ONLY, not any-block. This line called a local `containsBlockSeverity`
   * — the blanket predicate the severity policy REPLACED — so every advisory
   * class still barred export from the surface the user actually sees: a
   * section-depth warning, a distribution outlier, a keyword-drift finding or a
   * dead citation pointer each produced "Report failed delivery validation ·
   * export disabled". Measured 2026-08-05: four advisory classes disagreed with
   * the policy here, which made the truth-only gate cosmetic downstream.
   */
  if (invalidReason) {
    return {
      kind: 'invalid',
      exportBarred: true,
      exportHint: 'Report failed delivery validation — rerun it before export.',
      bannerTitle: 'Report failed delivery validation · export disabled',
      bannerDetail: invalidReason?.message
        ?? 'A blocking quality check failed. Review the report quality details, then rerun the report.',
      findings: [],
      findingCount: 0,
      action: 'rerun',
    };
  }

  // WITHHELD NARRATIVE (2026-09-18): no branch here on purpose. A withheld
  // leaf never bars export (owner decision), so a barred report is barred by
  // one of the states below and its banner names THAT reason; the withheld
  // leaf itself still reads "not assessed" in place.

  const bodyUnavailableReason = persistedReasons.find((reason) => BODY_UNAVAILABLE_REASON_CODES.has(reason.code));
  if (report._metadataOnly || bodyUnavailableReason) {
    return {
      kind: 'incomplete',
      exportBarred: true,
      exportHint: 'Report not ready for delivery — rerun it to create a validated export.',
      bannerTitle: 'Report not ready for delivery · export disabled',
      bannerDetail: report._metadataOnly
        ? 'Only report metadata is available. Load or rerun the full report before delivery.'
        : bodyUnavailableReason?.message
          ?? 'The complete validated report artifact is not available. Rerun the report before delivery.',
      findings: [],
      findingCount: 0,
      action: 'rerun',
    };
  }

  // Genuine draft-tier generation was retired. Persisted reports from that
  // era carry the explicit tier; `isDraft` alone is not sufficient because it
  // is also the publish-bar mirror used by quality holds.
  if (report.generationTier === 'draft') {
    return {
      kind: 'legacy-draft',
      exportBarred: true,
      exportHint: 'Finalize this legacy draft to export — regenerate it at investment-grade quality.',
      bannerTitle: 'Legacy draft · export disabled',
      bannerDetail: 'This report predates investment-grade-only generation. Finalize it to regenerate a validated deliverable.',
      findings: [],
      findingCount: 0,
      action: 'finalize',
    };
  }

  const incompleteReason = persistedReasons.find((reason) => INCOMPLETE_REASON_CODES.has(reason.code));
  if (incompleteReason) {
    return {
      kind: 'incomplete',
      exportBarred: true,
      exportHint: 'Report not ready for delivery — rerun it to create a validated export.',
      bannerTitle: 'Report not ready for delivery · export disabled',
      bannerDetail: incompleteReason.message,
      findings: [],
      findingCount: 0,
      action: 'rerun',
    };
  }

  return {
    kind: 'incomplete',
    exportBarred: true,
    exportHint: 'Report not ready for delivery — rerun it to create a validated export.',
    bannerTitle: 'Report not ready for delivery · export disabled',
    bannerDetail: 'The complete validated report artifact is not available. Rerun the report before delivery.',
    findings: [],
    findingCount: 0,
    action: 'rerun',
  };
}
